/** Retry transport loop for the standardized `tools/http/fetch` tool: backoff + Retry-After (capped at the config
 * ceiling) over an injected per-attempt executor; throws the normalized error on final failure. The delay and the error
 * normalizer are injected by the tools composition root (utils leaves may not import each other); the backoff math and
 * the HTTP-date parse are const-floor bodies (`const/backoff-math.ts`, `const/date-parse-format.ts`) read directly. */

import { calculateBackoff } from '@federa/const/backoff-math';
import { parseDate } from '@federa/const/date-parse-format';
import { DEFAULT_HTTP_RETRY_CONFIG } from '@federa/const/observability-retry';
import { STANDARD_HEADERS } from '@federa/const/http-header-names';
import { TIME_UNITS } from '@federa/const/time-units';
import type { FetchAttemptFn, FetchAttemptResult } from '@federa/types/http';
import type { HttpRetryConfig } from '@federa/types/http-api';

/** Host seams for the retry loop. `makeFetchFailedError` covers the no-response-no-error terminal (config with zero attempts). */
export interface RetryLoopDeps {
	attemptFn: FetchAttemptFn;
	delay: (ms: number) => Promise<void>;
	normalizeError: (error: unknown) => Error;
	makeFetchFailedError: () => Error;
}

/** The bound retry transport: one call = the whole attempt chain. */
export type RetryTransportFn = (url: string | URL | Request, init: RequestInit, retryConfig: Partial<HttpRetryConfig>) => Promise<Response>;

/** `Retry-After` header value in ms: numeric seconds, or an HTTP-date delta vs now; null when absent/unparseable. */
function parseRetryAfterHeader(value: string | null): number | null {
	if (!value) return null;
	const retryAfterSeconds = Number(value);
	if (Number.isFinite(retryAfterSeconds)) return Math.max(0, retryAfterSeconds * TIME_UNITS.SECOND_MS);
	const retryDate = parseDate(value);
	if (retryDate) return Math.max(0, retryDate.getTime() - Date.now());
	return null;
}

function backoffDelay(attemptNumber: number, config: HttpRetryConfig): number {
	return calculateBackoff(attemptNumber - 1, {
		baseDelayMs: config.initialDelayMs,
		maxDelayMs: config.maxDelayMs,
		backoffMultiplier: config.backoffMultiplier,
		maxAttempts: config.maxRetries,
	});
}

/** Next-retry delay: a response's Retry-After wins (capped at maxDelayMs), else exponential backoff. */
function getRetryDelay(input: { result: FetchAttemptResult; attempt: number; config: HttpRetryConfig }): number {
	const { result, attempt, config } = input;
	if (result.response) {
		const ms = parseRetryAfterHeader(result.response.headers.get(STANDARD_HEADERS.RETRY_AFTER));
		if (ms !== null) return Math.min(ms, config.maxDelayMs);
		return Math.min(backoffDelay(attempt + 1, config), config.maxDelayMs);
	}
	return backoffDelay(attempt + 1, config);
}

/** Bind the retry transport over `deps`: attempts run until a terminal result (non-retryable response/error) or the
 * attempt budget is spent, at which point the last error throws normalized (else the last response returns). */
export function makeRetryTransport(deps: RetryLoopDeps): RetryTransportFn {
	return async (url, init, retryConfig) => {
		const config: HttpRetryConfig = { ...DEFAULT_HTTP_RETRY_CONFIG, ...retryConfig };
		let lastError: unknown;
		let lastResponse: Response | undefined;
		for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
			const result = await deps.attemptFn({ url, options: init, config, attempt });
			if (result.response && !result.shouldRetry) return result.response;
			if (result.error && !result.shouldRetry) throw deps.normalizeError(result.error);
			lastError = result.error ?? lastError;
			lastResponse = result.response ?? lastResponse;
			if (result.shouldRetry) await deps.delay(getRetryDelay({ result, attempt, config }));
		}
		if (lastError) throw deps.normalizeError(lastError);
		if (lastResponse) return lastResponse;
		throw deps.makeFetchFailedError();
	};
}
